import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { ProjectLayout } from '@/components/layout/ProjectLayout'
import { useProject } from '@/hooks/useProjects'
import { useDefaultSequence } from '@/hooks/useSequences'
import { useCommits, useCreateCommit, useDeleteCommit } from '@/hooks/useCommits'
import { useVfxShots } from '@/hooks/useVfxShots'
import { useClips } from '@/hooks/useClips'
import { usePermissions } from '@/hooks/usePermissions'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog'
import {
  GitCommit,
  Calendar,
  User,
  Trash2
} from 'lucide-react'
import { useState, useEffect } from 'react'
import { toast } from 'sonner'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useCommitDiff, useCreateComparison } from '@/hooks/useCommits'
import type { ComparisonResponse } from '@/lib/api/commits'
import { CommitTimeline } from '@/components/commits/CommitTimeline'
import { OTIOTimelineViewer } from '@/components/commits/OTIOTimelineViewer'
import { CommitRenderingDialog } from '@/components/commits/CommitRenderingDialog'
import { useAnalytics } from '@/hooks/useAnalytics'

export function ProjectCommitsPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const { data: project, isLoading: projectLoading, error: projectError } = useProject(id)
  const { data: sequence, isLoading: sequenceLoading } = useDefaultSequence(id)
  const { data: commitsData, isLoading: commitsLoading } = useCommits(sequence?.id)
  const { data: vfxData } = useVfxShots(id || '', { limit: 500 })
  useClips(id) // Keep for future use
  const permissions = usePermissions(id, project?.owner_user_id)
  const createCommitMutation = useCreateCommit()
  const deleteCommitMutation = useDeleteCommit()
  const createComparisonMutation = useCreateComparison()
  const { trackTimelineCommitCreated, track } = useAnalytics()

  const [selectedCommit, setSelectedCommit] = useState<string | null>(null)
  const [_comparisonData, setComparisonData] = useState<ComparisonResponse | null>(null)

  // Dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [selectedOtioFile, setSelectedOtioFile] = useState<File | null>(null)
  const [commitMessage, setCommitMessage] = useState('')

  // Rendering progress dialog state
  const [renderingJobId, setRenderingJobId] = useState<string | null>(null)

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [commitToDelete, setCommitToDelete] = useState<string | null>(null)

  // Compare state - compareWithId is the commit we're comparing the selected commit against
  const [compareWithId, setCompareWithId] = useState<string>('')

  // OTIO loading state (fetch on-demand from Storage)
  const [otioData, setOtioData] = useState<{[commitId: string]: string}>({})
  const [otioLoading, setOtioLoading] = useState<{[commitId: string]: boolean}>({})

  const commits = commitsData?.commits || []

  // Fetch diff when comparing (selected commit vs compareWithId)
  const { data: diffData } = useCommitDiff(
    compareWithId || undefined,
    selectedCommit || undefined
  )
  const isLoading = projectLoading || sequenceLoading || commitsLoading

  // Auto-select commit from URL query parameter
  useEffect(() => {
    const commitId = searchParams.get('commit')
    if (commitId && commits.some(c => c.id === commitId)) {
      setSelectedCommit(commitId)
    }
  }, [searchParams, commits])

  // Reset compare when commit selection changes
  useEffect(() => {
    setCompareWithId('')
    setComparisonData(null)
  }, [selectedCommit])

  // Fetch OTIO data on-demand from Storage
  const fetchOtioData = async (commitId: string) => {
    // Skip if already loaded or loading
    if (otioData[commitId] || otioLoading[commitId]) {
      return
    }

    setOtioLoading(prev => ({ ...prev, [commitId]: true }))

    try {
      const { getCommitOtio } = await import('@/lib/api/commits')
      const otioJson = await getCommitOtio(commitId)
      setOtioData(prev => ({ ...prev, [commitId]: otioJson }))
    } catch (error) {
      console.error(`[OTIO] Failed to fetch for commit ${commitId}:`, error)
      toast.error('Failed to load timeline visualization')
    } finally {
      setOtioLoading(prev => ({ ...prev, [commitId]: false }))
    }
  }

  // Fetch OTIO when commit is selected
  useEffect(() => {
    if (selectedCommit) {
      fetchOtioData(selectedCommit)
    }
  }, [selectedCommit])

  // Fetch OTIO for comparison commit when needed
  useEffect(() => {
    if (compareWithId) {
      fetchOtioData(compareWithId)
    }
  }, [compareWithId])

  const handleGenerateComparison = async () => {
    if (!selectedCommit || !compareWithId) {
      toast.error('Please select two commits to compare')
      return
    }

    try {
      const result = await createComparisonMutation.mutateAsync({
        commitId: selectedCommit,
        request: {
          compare_to_commit_id: compareWithId,
          detect_vfx_changes: true,
          notify_team: false
        }
      })

      // Track comparison generation
      track('commits_compared', {
        project_id: id!,
        base_commit_id: selectedCommit,
        compare_commit_id: compareWithId,
      })

      setComparisonData(result)
    } catch {
      // Error already shown by mutation onError
    }
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      if (!file.name.endsWith('.otio') && !file.name.endsWith('.json')) {
        toast.error('Please select an OTIO file (.otio or .json)')
        return
      }
      setSelectedOtioFile(file)
    }
  }

  const handleCreateCommit = async () => {
    if (!selectedOtioFile || !commitMessage.trim()) {
      toast.error('Please provide both an OTIO file and commit message')
      return
    }

    if (!sequence) {
      toast.error('No sequence found. Please try again.')
      return
    }

    try {
      // Read file content
      const fileContent = await selectedOtioFile.text()

      // Validate it's valid JSON
      try {
        JSON.parse(fileContent)
      } catch (e) {
        throw new Error('Invalid OTIO file: not valid JSON')
      }

      // Create commit via API - this now returns a job response
      const jobResponse = await createCommitMutation.mutateAsync({
        sequence_id: sequence.id,
        message: commitMessage.trim(),
        otio_timeline: fileContent,
        metadata: {
          original_filename: selectedOtioFile.name
        }
      })

      // Track commit creation
      trackTimelineCommitCreated({
        project_id: id!,
        sequence_id: sequence.id,
        file_type: selectedOtioFile.name.endsWith('.otio') ? 'otio' : 'fcpxml',
      })

      // Close the create dialog and reset form
      setCreateDialogOpen(false)
      setSelectedOtioFile(null)
      setCommitMessage('')

      // Show the rendering progress dialog
      setRenderingJobId(jobResponse.job_id)
    } catch (error: any) {
      console.error('Create commit error:', error)
      toast.error(error.message || 'Failed to create commit')
    }
  }

  const handleSelectCompareWith = (value: string) => {
    // "none" means no comparison, just viewing single timeline
    setCompareWithId(value === 'none' ? '' : value)

    // Track compare dropdown selection (only track when selecting a commit to compare)
    if (value !== 'none') {
      track('timeline_view_toggled', {
        project_id: id!,
        view_mode: 'compare',
        base_commit_id: selectedCommit || undefined,
        compare_commit_id: value,
      })
    }
  }

  const handleCommitClick = (commitId: string) => {
    // Track commit viewed
    track('commit_viewed', {
      project_id: id!,
      commit_id: commitId,
    })
    setSelectedCommit(commitId)
  }

  const handleDeleteClick = (commitId: string) => {
    setCommitToDelete(commitId)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!commitToDelete || !sequence) return

    try {
      await deleteCommitMutation.mutateAsync({
        commitId: commitToDelete,
        sequenceId: sequence.id
      })

      // Track commit deletion
      track('commit_deleted', {
        project_id: id!,
        commit_id: commitToDelete,
      })

      // Clear selection if deleted commit was selected
      if (selectedCommit === commitToDelete) {
        setSelectedCommit(null)
      }

      // Clear comparison if deleted commit was being compared
      if (compareWithId === commitToDelete) {
        setCompareWithId('')
      }

      setDeleteDialogOpen(false)
      setCommitToDelete(null)
    } catch (error) {
      // Error already handled by mutation onError
    }
  }

  if (isLoading) {
    return (
      <ProjectLayout>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading project...</p>
          </div>
        </div>
      </ProjectLayout>
    )
  }

  if (projectError || !project) {
    return (
      <ProjectLayout>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <p className="text-destructive mb-4">Failed to load project</p>
            <Button onClick={() => navigate('/projects')}>Back to Projects</Button>
          </div>
        </div>
      </ProjectLayout>
    )
  }

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffDays = Math.floor(diffHours / 24)

    if (diffHours < 1) {
      return 'Just now'
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
    } else {
      return date.toLocaleDateString()
    }
  }

  const getCommitBadge = (commitNumber: number) => {
    return `#${commitNumber}`
  }

  return (
    <ProjectLayout projectName={project.name}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold tracking-tight">Commits</h2>
            <p className="text-muted-foreground">
              Timeline commits for {project.name}
            </p>
          </div>
          {permissions.canCreateCommits && (
            <Button onClick={() => setCreateDialogOpen(true)}>
              <GitCommit className="mr-2 h-4 w-4" />
              New Commit
            </Button>
          )}
        </div>

        {/* Empty State */}
        {commits.length === 0 && (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-12">
                <GitCommit className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No commits yet</h3>
                <p className="text-muted-foreground mb-6">
                  {permissions.canCreateCommits
                    ? 'Create your first commit by uploading an OTIO timeline export from your NLE'
                    : 'Commits will appear here once an admin or editor creates one'}
                </p>
                {permissions.canCreateCommits && (
                  <Button onClick={() => setCreateDialogOpen(true)}>
                    <GitCommit className="mr-2 h-4 w-4" />
                    Create First Commit
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* NLE-Style Timeline Visualization */}
        {commits.length > 0 && (
          <>
            <CommitTimeline
              commits={commits}
              selectedCommitId={selectedCommit}
              onSelectCommit={handleCommitClick}
            />

            {/* Commit Details Card (shown below timeline) */}
            {selectedCommit && (() => {
              const commit = commits.find(c => c.id === selectedCommit)
              let timelineName = commit?.commit_metadata?.timeline_name || 'Unnamed Timeline'
              let originalFilename = commit?.commit_metadata?.original_filename

              // Get clip and VFX counts from backend metadata (added during commit creation)
              let clipCount = commit?.commit_metadata?.clip_count || 0
              let vfxShotCount = commit?.commit_metadata?.vfx_shot_count || 0

              return (
                <Card>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="mt-1">
                          <GitCommit className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <div className="flex-1">
                          <CardTitle className="text-lg">
                            {timelineName}
                          </CardTitle>
                          <CardDescription className="text-sm mt-1 mb-2">
                            {commit?.message}
                          </CardDescription>
                          <CardDescription className="flex items-center gap-4">
                            <span className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              {commit?.author_name}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {formatTimestamp(commit?.created_at || '')}
                            </span>
                          </CardDescription>
                        </div>
                      </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="default">
                        {getCommitBadge(commits.find(c => c.id === selectedCommit)?.commit_number || 0)}
                      </Badge>
                      {permissions.canCreateCommits && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteClick(selectedCommit)}
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="border-t pt-4">
                    <h4 className="text-sm font-semibold mb-3">Timeline Contents</h4>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="p-3 bg-muted/30 rounded-lg">
                        <p className="text-xs text-muted-foreground mb-1">Total Clips</p>
                        <p className="text-2xl font-semibold">{clipCount}</p>
                      </div>
                      <div className="p-3 bg-muted/30 rounded-lg">
                        <p className="text-xs text-muted-foreground mb-1">VFX Shots</p>
                        <p className="text-2xl font-semibold">{vfxShotCount}</p>
                      </div>
                    </div>
                    <div className="space-y-2 text-sm text-muted-foreground">
                      {originalFilename && (
                        <p>Original File: <span className="font-mono">{originalFilename}</span></p>
                      )}
                      <p>Created: {new Date(commits.find(c => c.id === selectedCommit)?.created_at || '').toLocaleString()}</p>
                      <div className="mt-4">
                        <div className="flex items-center gap-2">
                          <Label htmlFor="compare-select" className="text-sm whitespace-nowrap">
                            Compare with:
                          </Label>
                          <Select
                            value={compareWithId || 'none'}
                            onValueChange={handleSelectCompareWith}
                            disabled={commits.length < 2}
                          >
                            <SelectTrigger id="compare-select" className="h-9 w-[240px]">
                              <SelectValue placeholder="None (single view)" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">None (single view)</SelectItem>
                              {commits
                                .filter(commit => commit.id !== selectedCommit)
                                .map((commit) => (
                                  <SelectItem key={commit.id} value={commit.id}>
                                    #{commit.commit_number} - {commit.message.slice(0, 30)}
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Generate Comparison Button (AIO-46) */}
                        {permissions.canGenerateComparisons && compareWithId && compareWithId !== 'none' && (
                          <div className="mt-3 p-3 bg-muted/50 rounded-lg border">
                            <p className="text-xs text-muted-foreground mb-2">
                              Click to generate on-demand comparison
                            </p>
                            <Button
                              onClick={handleGenerateComparison}
                              disabled={createComparisonMutation.isPending}
                              size="sm"
                              className="w-full"
                            >
                              {createComparisonMutation.isPending ? (
                                <>
                                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-2" />
                                  Generating Comparison...
                                </>
                              ) : (
                                <>
                                  <GitCommit className="h-3 w-3 mr-2" />
                                  Generate Comparison
                                </>
                              )}
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* OTIO Timeline Visualization (loaded on-demand from Storage) */}
                    {selectedCommit && otioData[selectedCommit] && otioData[selectedCommit].length > 0 && !otioLoading[selectedCommit] && (() => {
                      const currentOtioData = otioData[selectedCommit]
                      return (
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">Timeline Viewer</Label>
                          <OTIOTimelineViewer
                            otioTimeline={currentOtioData}
                            compareOtioTimeline={compareWithId && otioData[compareWithId] ? otioData[compareWithId] : undefined}
                            diffData={compareWithId && diffData ? {
                              changes: diffData.changes,
                              vfx_changes: diffData.vfx_changes,
                              summary: diffData.summary
                            } : undefined}
                            vfxShots={vfxData?.shots || []}
                          />
                        </div>
                      )
                    })()}

                    {/* Loading indicator */}
                    {selectedCommit && (otioLoading[selectedCommit] || (!otioData[selectedCommit] && !otioLoading[selectedCommit])) && (
                      <div className="text-sm text-muted-foreground">
                        Loading timeline visualization...
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
              )
            })()}
          </>
        )}
      </div>

      {/* Create Commit Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-[525px]">
          <DialogHeader>
            <DialogTitle>Create Timeline Commit</DialogTitle>
            <DialogDescription>
              Upload an OTIO timeline file exported from your NLE (Premiere, Avid, Resolve, etc.)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="otio-file">OTIO Timeline File</Label>
              <Input
                id="otio-file"
                type="file"
                accept=".otio,.json"
                onChange={handleFileChange}
                className="cursor-pointer"
              />
              {selectedOtioFile && (
                <p className="text-sm text-muted-foreground">
                  Selected: {selectedOtioFile.name}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="commit-message">Commit Message</Label>
              <Textarea
                id="commit-message"
                placeholder="Describe the changes in this timeline version..."
                value={commitMessage}
                onChange={(e) => setCommitMessage(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCreateDialogOpen(false)}
              disabled={createCommitMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateCommit}
              disabled={!selectedOtioFile || !commitMessage.trim() || createCommitMutation.isPending}
            >
              {createCommitMutation.isPending ? 'Creating...' : 'Create Commit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Commit</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this commit? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {commitToDelete && (
              <div className="space-y-2 text-sm">
                <p><strong>Commit #{commits.find(c => c.id === commitToDelete)?.commit_number}</strong></p>
                <p className="text-muted-foreground">
                  {commits.find(c => c.id === commitToDelete)?.message}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleteCommitMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleteCommitMutation.isPending}
            >
              {deleteCommitMutation.isPending ? 'Deleting...' : 'Delete Commit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rendering Progress Dialog */}
      {renderingJobId && sequence && (
        <CommitRenderingDialog
          jobId={renderingJobId}
          sequenceId={sequence.id}
          onComplete={(commitId) => {
            // Job completed successfully - refresh commits list and select the new commit
            queryClient.invalidateQueries({ queryKey: ['commits', sequence.id] })
            queryClient.invalidateQueries({ queryKey: ['activityLogs'] })
            queryClient.invalidateQueries({ queryKey: ['clips'] })
            queryClient.invalidateQueries({ queryKey: ['vfx-shots'] })

            toast.success('Timeline commit created successfully!')
            setRenderingJobId(null)
            setSelectedCommit(commitId)
          }}
          onError={(error) => {
            // Job failed - show error and clear rendering state
            toast.error(error)
            setRenderingJobId(null)
          }}
        />
      )}
    </ProjectLayout>
  )
}
